import { Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import loginIllustration from "../../../../assets/main_illustrations/auth/login.jpg";
import { AuthContainer } from "../../../../utils/components/AuthScaffold";
import CertiblokIcon from "../../../../utils/components/icons/CertiblokIcon";
import LoadingTextButton from "../../../../utils/components/LoadingTextButton";
import { useBetterMediaQuery } from "../../../../utils/hooks/useBetterMediaQuery";
import { useLoginRedirect } from "../../hooks/useLoginRedirect";
import { authSSOUser, useAbilitiesLoader } from "./hooks/useAbilitiesLoader";

export function LoginSSOPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const from = (location.state as any)?.from || "/";

	const [searchParams] = useSearchParams();
	const isSyncLogin = searchParams.get("platform") === "sync";

	const loginRedirect = useLoginRedirect({
		isSyncLogin,
	});

	useAbilitiesLoader({
		isSyncLogin,
		onSuccess: (abilities) => {
			loginRedirect(abilities);
		},
	});

	const { mutate, isLoading } = useMutation({
		mutationFn: () => authSSOUser(true),
	});

	const isMd = useBetterMediaQuery("md");

	return (
		<>
			<img
				src={loginIllustration}
				alt={t("login.loginIllustration")}
				className="absolute top-0 left-0 w-full h-full object-cover object-left"
			/>
			<AuthContainer>
				<div className="flex flex-col h-full">
					<div
						className="group flex items-center gap-2 cursor-pointer"
						onClick={() => {
							localStorage.clear();
							sessionStorage.clear();
							Cookies.remove("CertiblokAccessToken");
							Cookies.remove("CertiblokRefreshToken");
							Cookies.remove("CertiblokAuthOverride");
							navigate("/login", { replace: true, state: { from } });
						}}
					>
						<CertiblokIcon
							size={24}
							color="primary"
							name={"arrow_mini_outline_left"}
							className="group-hover:-translate-x-1 transition-transform"
						/>
						<h6 className="text-primary text-h6 md:text-h5 font-extrabold">{t("login.sso.title")}</h6>
					</div>

					<div className="flex flex-col items-center justify-center w-full flex-grow pt-12 md:pt-16">
						<div
							className={twMerge(
								clsx(
									"flex items-center justify-center w-14 h-14 md:w-[72px] md:h-[72px] rounded-full animate-spin",
									"bg-[rgba(10,132,255,0.12)] [&_>i]:text-[rgba(10,132,255,1)]"
								)
							)}
						>
							<CertiblokIcon size={isMd ? 48 : 32} color={"#0A84FF"} name={"spinner"} />
						</div>
						<Typography
							sx={{
								typography: {
									xs: "subtitle1",
									md: "h4",
								},
							}}
							className={twMerge(clsx("text-black md:font-normal mt-4 md:mt-6", "font-semibold text-center text-black"))}
						>
							{t("syncAuthDialog.title")}
						</Typography>
						<Typography
							sx={{
								typography: {
									xs: "caption",
									md: "h5",
								},
							}}
							className={twMerge(clsx("font-normal text-black-a60 pt-1 md:pt-2", "text-center text-gray-600 mb-6"))}
						>
							{isSyncLogin ? t("syncAuthDialog.description") : t("login.sso.description")}
						</Typography>
					</div>
					{isSyncLogin ? (
						<div className="flex items-center justify-between w-full pt-4 border-t border-gray-100 gap-2 mt-auto">
							<Typography variant="body2" color="text.secondary">
								{t("syncAuthDialog.notAuthed")}
							</Typography>
							<LoadingTextButton
								loading={isLoading}
								onClick={() => {
									mutate();
								}}
								variant="contained"
								color="primary"
								disableElevation
								sx={{ textTransform: "none" }}
							>
								{t("syncAuthDialog.actionButton")}
							</LoadingTextButton>
						</div>
					) : null}
				</div>
			</AuthContainer>
		</>
	);
}
